import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/** The browser history entry's state as react-router stores it; `usr` is `location.state`. */
interface IRouterHistoryState {
  usr?: unknown
}

/**
 * A search param naming what a page has open (a panel, a modal, a record), so a link — or Arc,
 * which can navigate but not click — lands on it. Writes replace the history entry and keep every
 * other param, the hash and the location state.
 *
 * A write is deferred and applied to the live URL, and dropped if the page has been left: a
 * close that rides along with a navigation (pick a channel, panel closes) must not replace the
 * entry the navigation is still on its way to.
 */
interface IUrlOpenParamOptions {
  /** Params that only locate what is open, removed with it when it closes. */
  clearOnClose?: readonly string[]
}

export const useUrlOpenParam = (
  name: string,
  { clearOnClose }: IUrlOpenParamOptions = {},
): [string | null, (value: string | null) => void] => {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const value = new URLSearchParams(search).get(name)
  // A key, not the array, so a caller's inline list doesn't remake the setter every render.
  const clearOnCloseKey = clearOnClose?.join('&') ?? ''

  const setValue = useCallback(
    (next: string | null) => {
      setTimeout(() => {
        if (window.location.pathname !== pathname) return
        const params = new URLSearchParams(window.location.search)
        const linked = clearOnCloseKey ? clearOnCloseKey.split('&') : []
        const hasLinked = next === null && linked.some((param) => params.has(param))
        if (params.get(name) === next && !hasLinked) return
        if (next === null) {
          params.delete(name)
          linked.forEach((param) => params.delete(param))
        } else params.set(name, next)
        const query = params.toString()
        void navigate(
          { pathname, search: query ? `?${query}` : '', hash: window.location.hash },
          {
            replace: true,
            state: (window.history.state as IRouterHistoryState | null)?.usr,
          },
        )
      })
    },
    [name, pathname, navigate, clearOnCloseKey],
  )

  return [value, setValue]
}

const FLAG_ON = 'open'

/**
 * A yes/no param: `?name=open` while something is open, absent otherwise. Returns
 * whether it is on, and stable callbacks to turn it on and off.
 */
export const useUrlFlag = (name: string): [boolean, () => void, () => void] => {
  const [value, setValue] = useUrlOpenParam(name)
  const turnOn = useCallback(() => setValue(FLAG_ON), [setValue])
  const turnOff = useCallback(() => setValue(null), [setValue])
  return [value === FLAG_ON, turnOn, turnOff]
}

const asSlug = <T extends string>(option: T): string => option

/**
 * One of a fixed set of options (a tab), kept in the URL by its slug. The default
 * is the absent param, and anything unrecognised reads as the default. `slugOf`
 * should be defined outside the component.
 */
export const useUrlEnumParam = <T extends string>(
  name: string,
  options: readonly T[],
  defaultValue: T,
  slugOf: (option: T) => string = asSlug,
): [T, (next: T) => void] => {
  const [param, setParam] = useUrlOpenParam(name)
  const value = options.find((option) => slugOf(option) === param) ?? defaultValue
  const setValue = useCallback(
    (next: T) => setParam(next === defaultValue ? null : slugOf(next)),
    [setParam, defaultValue, slugOf],
  )
  return [value, setValue]
}

type Resolved<T> = T | null | undefined

interface IOpenOnParamOptions {
  /** An async `resolve` found nothing for this value; e.g. strip the param. */
  onMissing?: (value: string) => void
}

const isPromise = <T>(value: unknown): value is Promise<T> =>
  !!value && typeof (value as Promise<T>).then === 'function'

/**
 * Calls `open` once each time `value` arrives — on first load or a later navigation — as soon as
 * `resolve` can turn it into something to open (e.g. once the list holding the record has loaded).
 * A sync null means "not yet" and is retried; a promise (a fetch for a record past the loaded
 * page) settles the value either way, calling `onMissing` when it finds nothing. A value that
 * changed while its promise was out is not opened. `resolve`, `open` and `onMissing` should be
 * memoized.
 */
export const useOpenOnParam = <T>(
  value: string | null,
  resolve: (value: string) => Resolved<T> | Promise<Resolved<T>>,
  open: (item: T) => void,
  { onMissing }: IOpenOnParamOptions = {},
): void => {
  const handledRef = useRef<string | null>(null)

  useEffect(() => {
    if (!value) {
      handledRef.current = null
      return
    }
    if (value === handledRef.current) return
    const item = resolve(value)
    if (item === null || item === undefined) return
    handledRef.current = value
    if (!isPromise<Resolved<T>>(item)) {
      open(item)
      return
    }
    const settle = (found: Resolved<T>) => {
      if (handledRef.current !== value) return
      if (found === null || found === undefined) onMissing?.(value)
      else open(found)
    }
    // A failed lookup is the same as finding nothing.
    void item.then(settle, () => settle(null))
  }, [value, resolve, open, onMissing])
}
